(() => {
  const selector = '[data-bm-market-suggest]';
  const storageKey = 'bm_market_suggestion_v1';
  const allowed = new Set(['BE', 'NL']);
  const consentKeys = ['preferences', 'analytics', 'marketing'];

  // A direct "Hier blijven" / country-switch choice is a strictly functional
  // locale preference, not analytics or marketing consent. Remember it even when
  // the visitor rejects Shopify's optional cookies; never modify their consent.
  const readDismissal = (pair) => {
    for (const name of ['localStorage', 'sessionStorage']) {
      try {
        const store = window[name];
        const raw = store?.getItem(storageKey);
        if (!raw) continue;
        const saved = JSON.parse(raw);
        const expiry = Number(saved?.expires);
        if (saved?.pair !== pair || !Number.isFinite(expiry)) continue;
        if (Date.now() < expiry) return true;
        store.removeItem(storageKey);
      } catch (_) { /* The browser may block storage. */ }
    }
    return false;
  };

  const remember = (pair, days) => {
    const value = JSON.stringify({
      pair,
      expires: Date.now() + (days * 86400000)
    });
    for (const name of ['localStorage', 'sessionStorage']) {
      try { window[name]?.setItem(storageKey, value); }
      catch (_) { /* Keep functional UI usable without storage. */ }
    }
  };

  const hasConsentDecision = (consent = {}) =>
    consentKeys.every((key) => consent[key] === 'yes' || consent[key] === 'no');

  const waitForCookieChoice = (callback) => {
    const inspect = () => {
      const api = window.Shopify?.customerPrivacy;
      if (!api || typeof api.currentVisitorConsent !== 'function') return false;
      return hasConsentDecision(api.currentVisitorConsent());
    };

    let resolved = false;
    const finish = () => {
      if (resolved) return;
      resolved = true;
      document.removeEventListener('bm:cookie-consent-resolved', finish);
      document.removeEventListener('visitorConsentCollected', finish);
      callback();
    };

    if (inspect()) {
      finish();
      return;
    }

    document.addEventListener('bm:cookie-consent-resolved', finish, { once: true });
    document.addEventListener('visitorConsentCollected', finish, { once: true });

    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => {
          if (error) return;
          if (inspect()) finish();
        }
      );
    }
  };

  const initMarket = (root = document) => {
    const el = root.matches?.(selector) ? root : root.querySelector?.(selector);
    if (!el || el.dataset.bmMarketReady === 'true') return;
    el.dataset.bmMarketReady = 'true';

    const current = String(window.Shopify?.country || el.dataset.currentCountry || '').toUpperCase();
    if (!allowed.has(current)) return;

    const routeRoot = window.Shopify?.routes?.root || '/';
    const endpoint =
      `${routeRoot}browsing_context_suggestions.json?country[enabled]=true&country[exclude]=${encodeURIComponent(current)}&language[enabled]=false`;

    fetch(endpoint, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) throw new Error('Suggestion request failed');
        return response.json();
      })
      .then((data) => {
        const suggested = String(data?.suggestions?.[0]?.parts?.country?.handle || '').toUpperCase();
        if (!allowed.has(suggested) || suggested === current) return;

        const pair = `${current}:${suggested}`;
        if (readDismissal(pair)) return;

        const isBelgium = suggested === 'BE';
        const heading = isBelgium ? el.dataset.beHeading : el.dataset.nlHeading;
        const text = isBelgium ? el.dataset.beText : el.dataset.nlText;
        const button = isBelgium ? el.dataset.beButton : el.dataset.nlButton;
        const headingEl = el.querySelector('[data-bm-market-heading]');
        const textEl = el.querySelector('[data-bm-market-text]');
        const flagEl = el.querySelector('[data-bm-market-flag]');
        const switchButton = el.querySelector('[data-bm-market-switch]');
        const input = el.querySelector('[data-bm-market-country-input]');
        const stayControls = [...el.querySelectorAll('[data-bm-market-stay]')];

        if (!headingEl || !textEl || !switchButton || !input || !input.form) return;

        headingEl.textContent = heading || '';
        textEl.textContent = text || '';
        switchButton.textContent = button || '';
        if (flagEl) {
          flagEl.classList.remove('is-be', 'is-nl');
          flagEl.classList.add(isBelgium ? 'is-be' : 'is-nl');
        }

        const days = Math.max(1, Number(el.dataset.rememberDays) || 30);
        const previousFocus = document.activeElement;
        let closing = false;

        const closeAndRemember = () => {
          if (closing) return;
          closing = true;
          remember(pair, days);
          el.classList.add('is-leaving');
          el.classList.remove('is-visible');
          document.body.classList.remove('bm-market-modal-open');

          window.setTimeout(() => {
            el.hidden = true;
            el.classList.remove('is-leaving');
            if (previousFocus && typeof previousFocus.focus === 'function') {
              previousFocus.focus({ preventScroll: true });
            }
          }, 320);
        };

        switchButton.addEventListener('click', () => {
          // The country choice must also be remembered when switching away.
          remember(pair, days);
          input.value = suggested;
          switchButton.disabled = true;
          switchButton.setAttribute('aria-busy', 'true');
          if (typeof input.form.requestSubmit === 'function') input.form.requestSubmit();
          else input.form.submit();
        }, { once: true });

        stayControls.forEach((control) =>
          control.addEventListener('click', closeAndRemember)
        );

        const onKeydown = (event) => {
          if (el.hidden) return;

          if (event.key === 'Escape') {
            event.preventDefault();
            closeAndRemember();
            return;
          }

          if (event.key !== 'Tab') return;

          const focusable = [
            ...el.querySelectorAll(
              'button:not([disabled]),a[href],input:not([type="hidden"]),select,textarea,[tabindex]:not([tabindex="-1"])'
            )
          ].filter((node) => !node.hidden && node.offsetParent !== null);

          if (!focusable.length) return;

          const first = focusable[0];
          const last = focusable[focusable.length - 1];

          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        };

        document.addEventListener('keydown', onKeydown);

        el.hidden = false;
        document.body.classList.add('bm-market-modal-open');

        requestAnimationFrame(() => requestAnimationFrame(() => {
          el.classList.add('is-visible');
          window.setTimeout(() => switchButton.focus({ preventScroll: true }), 180);
        }));
      })
      .catch(() => {});
  };

  const init = (root = document) => {
    const el = root.matches?.(selector) ? root : root.querySelector?.(selector);
    if (!el || el.dataset.bmMarketConsentWait === 'true' || el.dataset.bmMarketReady === 'true') return;
    el.dataset.bmMarketConsentWait = 'true';

    waitForCookieChoice(() => {
      delete el.dataset.bmMarketConsentWait;
      initMarket(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
